import { useEffect, useState } from "react";
import { Palette, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { HOLIDAYS, holidayForDate } from "../lib/holidays";

const PRESETS = ["#00E5FF", "#0066FF", "#FF2A5F", "#B266FF", "#31A24C", "#FF7A00", "#FFD700", "#FFFFFF"];

export const ThemeEditor = () => {
  const [cfg, setCfg] = useState({ glow_color: "#00E5FF", holiday: "auto", glow_intensity: 100, greeting: true, particles: "normal", grain: false });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/site-theme").then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  const preview = (next) => { setCfg(next); window.dispatchEvent(new CustomEvent("kv-theme", { detail: next })); };
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/site-theme", cfg); toast.success("Site theme saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const autoNow = holidayForDate();

  return (
    <div className="bg-[#0A0A0A] border border-[#1C1C1F] rounded-2xl p-5" data-testid="theme-editor">
      <p className="text-white font-heading uppercase tracking-widest text-xs flex items-center gap-2 mb-4"><Palette size={15} className="text-[#00E5FF]" /> Glow &amp; Holiday Theme</p>
      <p className="font-mono text-[10px] uppercase tracking-wider text-[#52525B] mb-2">Glow colour (screen + cards)</p>
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {PRESETS.map((c) => (
          <button key={c} data-testid={`glow-preset-${c.slice(1)}`} onClick={() => preview({ ...cfg, glow_color: c })} title={c}
            className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${cfg.glow_color.toUpperCase() === c ? "border-white" : "border-transparent"}`}
            style={{ background: c, boxShadow: `0 0 12px ${c}` }} />
        ))}
        <input data-testid="glow-color-input" type="color" value={cfg.glow_color} onChange={(e) => preview({ ...cfg, glow_color: e.target.value.toUpperCase() })}
          className="w-10 h-8 bg-transparent border border-white/10 rounded cursor-pointer" />
        <span className="font-mono text-[11px] text-[#A1A1AA]">{cfg.glow_color}</span>
      </div>
      <p className="font-mono text-[10px] uppercase tracking-wider text-[#52525B] mb-2">Glow brightness · {cfg.glow_intensity}%</p>
      <input data-testid="glow-intensity-slider" type="range" min="0" max="150" step="5" value={cfg.glow_intensity}
        onChange={(e) => preview({ ...cfg, glow_intensity: Number(e.target.value) })} className="w-full sm:w-72 mb-5 accent-[#00E5FF]" />
      <p className="font-mono text-[10px] uppercase tracking-wider text-[#52525B] mb-2">Animated holiday background (main pages)</p>
      <select data-testid="holiday-select" value={cfg.holiday} onChange={(e) => preview({ ...cfg, holiday: e.target.value })}
        className="bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-sm text-white mb-1 w-full sm:w-auto">
        <option value="auto">Automatic by date{autoNow ? ` (now: ${HOLIDAYS[autoNow].label})` : " (none today)"}</option>
        <option value="none">Off</option>
        {Object.entries(HOLIDAYS).map(([k, h]) => <option key={k} value={k}>{h.label}</option>)}
      </select>
      <div className="flex flex-wrap items-center gap-4 my-4">
        <label className="flex items-center gap-2 text-sm text-[#A1A1AA]">Particles
          <select data-testid="particles-select" value={cfg.particles} onChange={(e) => preview({ ...cfg, particles: e.target.value })}
            className="bg-[#141414] border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white">
            <option value="off">Off</option><option value="low">Light</option><option value="normal">Normal</option><option value="high">Heavy</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-[#A1A1AA] cursor-pointer">
          <input data-testid="greeting-toggle" type="checkbox" checked={cfg.greeting} onChange={(e) => preview({ ...cfg, greeting: e.target.checked })} className="accent-[#00E5FF]" />
          Holiday greeting in announcement bar
        </label>
        <label className="flex items-center gap-2 text-sm text-[#A1A1AA] cursor-pointer">
          <input data-testid="grain-toggle" type="checkbox" checked={cfg.grain} onChange={(e) => preview({ ...cfg, grain: e.target.checked })} className="accent-[#00E5FF]" />
          Film grain overlay
        </label>
      </div>
      <p className="font-mono text-[10px] text-[#52525B] mb-4">Changes preview instantly on your screen; save to apply for everyone.</p>
      <button data-testid="theme-save-btn" onClick={save} disabled={saving}
        className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#00E5FF] text-black font-heading uppercase tracking-widest text-[11px] hover:bg-white transition-colors disabled:opacity-50">
        {saving && <Loader2 size={13} className="animate-spin" />} Save theme
      </button>
    </div>
  );
};
